import type {
	DuplicateResolutionAction,
	DuplicateScenario,
} from '@/modules/worker/services/types';
import type { IAddress } from '@/types/address';
import type { DataSet, DB, ObjectID, Timestamp } from '@/types/common';
import type { InterimWorkerDocuments } from '@/types/documents';
import type { IInterimJob, IWorkerJob, JobService } from '@/types/job';
import type { IMissionDB } from '@/types/mission';
import type { IUser } from '@/types/user';

export enum Gender {
	Male = 'male',
	Female = 'female',
}

export enum UserStatus {
	Invited = 'invited',
	Registered = 'registered',
	Imported = 'imported',
	Inactive = 'inactive',
	NoLongerRegistered = 'no-longer-registered',
}

export enum CandidateStatus {
	new = 'new',
	rejected = 'rejected',
	integrated = 'integrated',
	interesting = 'interesting',
	meeting = 'meeting',
	met = 'met',
	toFollow = 'toFollow',
}

export enum CandidateOrigin {
	SANS_ORIGINE = 'Sans origine',
	EMPTY = '',

	ANNONCE_AUTRES_JOB_BOARD = 'Annonce autres Job Board',
	ANNONCE_COOKORICO = 'Annonce Cookorico',
	ANNONCE_EMPLOI_SOIGNANT = 'Annonce Emploi Soignant',
	ANNONCE_FRANCE_TRAVAIL = 'Annonce France travail (pôle emploi)',
	ANNONCE_HELLOWORK = 'Annonce Hellowork',
	ANNONCE_INDEED = 'Annonce Indeed',
	ANNONCE_JOBTRANSPORT = 'Annonce Jobtransport',
	ANNONCE_LE_BON_COIN = 'Annonce Le Bon Coin.fr',
	ANNONCE_LINKEDIN = 'Annonce Linkedin',
	ANNONCE_METEO_JOB = 'Annonce Météo Job',
	ANNONCE_OUEST_FRANCE = 'Annonce Ouest France',
	ANNONCE_SITE_INTERNET_GROUPE = 'Annonce Site Internet Groupe',
	ANNONCE_STAFFSANTE = 'Annonce Staffsanté',
	ANNONCE_VIGIJOBS = 'Annonce Vigijobs',

	BOUCHE_A_OREILLE = 'Bouche à oreille',
	BRETAGNE_ATELIERS_CAP_EMPLOI = 'Bretagne Ateliers / Cap Emploi',
	BUS_INTERACTION = 'Bus Interaction',

	CANDIDATURE_SPONTANEE_LIBELLE = 'Candidature spontanée',
	CENTRE_APPEL = "Centre d'appel",

	CVTHEQUE_COOKORICO = 'Cvthèque Cookorico',
	CVTHEQUE_EMPLOI_SOIGNANT = 'Cvthèque Emploi Soignant',
	CVTHEQUE_FRANCE_TRAVAIL = 'Cvthèque France Travail (ex pôle emploi)',
	CVTHEQUE_HELLOWORK = 'Cvthèque Hellowork',
	CVTHEQUE_INDEED = 'Cvthèque Indeed - Smartsourcing',
	CVTHEQUE_METEOJOB = 'Cvthèque Meteojob',
	CVTHEQUE_SITE_INTERNET_GROUPE = 'Cvthèque site internet Groupe',

	ECOLES_FORMATIONS = 'Ecoles/Formations',
	ENVOYEE_PAR_LE_CLIENT = 'Envoyée par le client',

	FACEBOOK = 'Facebook',
	MERCIII = 'Merciii',

	PARRAINAGE = 'Parrainage',
	PARTENARIATS_FRANCE_TRAVAIL_MISSION_LOCALE = 'Partenariats (France Travail, Mission locale...)',
	PASSAGE_AGENCE_ANNONCE_VITRINE = 'Passage Agence / Annonce Vitrine',
	RADIO_PRESSE = 'Radio, Presse...',

	REPRISE_CELTIC = 'Reprise Celtic',
	REPRISE_EBC = 'Reprise EBC',
	REPRISE_GEZIM = 'Reprise Gezim',

	SALON_FORUM = 'Salon / Forum',
}

export enum TeamPreference {
	alone = 'alone',
	team = 'team',
	both = 'both',
}

export enum ESearchWorkerModes {
	INTERIM = 'interim',
	CANDIDATE = 'candidate',
	EVOLIA = 'evolia',
}

export enum ECandidateContact {
	contact = 'contact',
	voiceMessage = 'voice-message',
	phoneReport = 'phone-report',
}

export enum EWorkerDatabaseOrigin {
	INTERIM = 'interim',
	CANDIDATE = 'candidate',
	EVOLIA = 'evolia',
}

export enum CVType {
	MAIN = 'MAIN',
	ANONYMIZED = 'ANONYMIZED',
	GENERATED = 'GENERATED',
	OTHER = 'OTHER',
}

export enum EValidationStatus {
	OK = 'ok',
	INVALID = 'invalid',
	UNKNOWN = 'unknown',
	HARD_BOUNCE = 'hard_bounce',
	BLOCKED = 'blocked',
}

export type WorkerType = 'interim' | 'candidate';

export interface IRegularAvailabilities {
	morning: boolean;
	afternoon: boolean;
	evening: boolean;
	night: boolean;
}

export interface Environment {
	negativeCold: boolean;
	positiveCold: boolean;
	noise: boolean;
	smells: boolean;
	dust: boolean;
	sustainedPace: boolean;
	extendedStandingPosition: boolean;
	heavyLoads: boolean;
}

export interface Schedule {
	'2x8': boolean;
	'3x8': boolean;
	'4x8': boolean;
	day: boolean;
}

export interface ContractPreference {
	CDI: boolean;
	CDD: boolean;
	INTERIM: boolean;
	VACATION: boolean;
}

export interface WorkerPreferences {
	environment: Environment;
	regularAvailabilities: Array<IRegularAvailabilities>;
	weekendShift: boolean;
	nightShift: boolean;
	vehicle: boolean;
	maxTravelTime: number;
	maxTravelDistance: number;
	schedule: Schedule;
	contractPreference: ContractPreference;
	carpooling: boolean;
	teamPreference: TeamPreference;
	salaryExpected?: number;
}

export interface IWorkerAgency {
	agencyId: ObjectID;
	agencyName: string;
	candidateStatus: CandidateStatus;
	createdAt?: number;
	deleted?: boolean;
	inactive?: boolean;
	contactDetails: {
		called?: {
			collaboratorId: ObjectID;
			collaboratorName: string;
			timestamp: Timestamp;
			type: ECandidateContact;
		};
		vocalMessage?: {
			collaboratorId: ObjectID;
			collaboratorName: string;
			timestamp: Timestamp;
			type: ECandidateContact;
		};
		phoneReport?: {
			collaboratorId: ObjectID;
			collaboratorName: string;
			timestamp: Timestamp;
			type: ECandidateContact;
		};
	};
}

export interface IOfferApplication {
	candidateStatus: CandidateStatus;
	offerId: ObjectID;
	offerName: string;
	jobId: ObjectID;
	applicationDate: Timestamp;
	client: string;
}

export interface IHabilitation {
	name: string;
	type: string;
	code: string;
	active?: boolean;
	expirationDate?: number;
}

export interface IWorkerHabilitation {
	idHabilitation: string;
	habilitationName: string;
	acquisitionDate?: number;
	expirationDate?: number;
	note?: string;
	type: string;
	isValid?: boolean;
}

export interface IAdministrativeDocuments {
	habilitations?: IHabilitation[];
	BTPCard?: Timestamp;
	medicalCheckup?: Timestamp;
	residencePermit?: Timestamp;
}

export type CV = {
	path: string | null;
	registrationDate?: Timestamp;
	collaboratorId?: ObjectID;
};

export interface IInterview {
	date: Timestamp;
	candidateId: ObjectID;
	collaboratorId: ObjectID;
	candidateMessage?: string;
	collaboratorMessage?: string;
	closed: boolean;
	comment?: string;
	start: number;
	end: number;
}

export interface IInterviewDB extends IInterview, DB {}

export interface IInterim extends IUser {
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	blacklist?: any;
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	stats?: any;
	jobs: Array<IInterimJob>;
	workerDocuments: InterimWorkerDocuments;

	nir?: string; // social security number
	nirKey?: string; // social security number - key

	// Evolia fields for synchroinzation
	MatriculeSalarieEvolia?: string;
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	agenciesList?: any[];
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	agencies?: any[];
	evoliaId?: number;

	managerId?: ObjectID;
	regularAvailabilities: Array<IRegularAvailabilities>;
	unavailabilities?: Array<ObjectID>;
	nightShift: boolean;
	weekendShift: boolean;
	vehicle: boolean;
	enableEmail: boolean;
	enableSMS: boolean;
	phone: string;
	address?: IAddress;
	evoliaAddress?: IAddress;
	cv: CV;
	state?: string;
	maxTravelDistance?: number;
	maxTravelTime?: number;
	carpooling?: boolean;
	publicResume?: string;
	gender?: Gender;
	birthDate?: number;
	birthPlace?: string;
	birthCountry?: string;
	invitationDate?: number;
	oldNotes?: { [key: string]: string };
	presentationText: { [key: string]: string };
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	inactiveByAgencies?: any;
	nationalityCode: string;
	missions?: {
		currentMissions?: IMissionDB[];
		futureMissions?: IMissionDB[];
		lastMissions?: IMissionDB[];
	};
	administrativeDocuments?: IAdministrativeDocuments;
	databaseOrigin?: ESearchWorkerModes;
	lastAgencyContact?: {
		timestamp: number;
		collaboratorIdentity: string;
		collaboratorId: ObjectID;
	} | null;
	interviews?: IInterviewDB[];
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	askedChanges?: any;
	emailValidity?: EValidationStatus;
	phoneValidity?: EValidationStatus;
}

export interface IInterimDB extends IInterim, DB {}

export type CandidateItem = {
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	value: any;
	validated: boolean;
};

export type CandidateParsed = {
	firstname?: CandidateItem;
	lastname?: CandidateItem;
	email?: CandidateItem;
	phone?: CandidateItem;
	address?: CandidateItem;
	picture?: CandidateItem;
	cv_path?: CandidateItem;
	jobs?: Array<CandidateItem>;
	birthDate?: CandidateItem;
	birthPlace?: CandidateItem;
	birthCountry?: CandidateItem;
	keywords?: CandidateItem;
};

export interface ICandidate {
	firstname?: string;
	lastname?: string;
	email?: string;
	phone?: string;
	address?: IAddress;
	picture?: string | null;
	cv: CV;
	anonymizedCv?: CV;
	jobOffers: {
		jobOfferId: string;
		name: string;
		appliedAt: Timestamp;
	}[];
	sourceJobService?: JobService;
	origin?: string;
	source?: string;
	workerId?: ObjectID;
	agencies?: IWorkerAgency[];
	status: CandidateStatus;
	timestamp?: DataSet<Timestamp>;
	jobs?: Array<IWorkerJob>;
	birthDate?: number;
	birthPlace?: string;
	birthCountry?: string;
	fillingRate?: number;
	keywords?: Array<string>;
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	dataAnalysis?: any;
	candidateParsed?: CandidateParsed;
	interviews: Array<IInterviewDB>;
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	availableCommands?: any;
	vehicle?: boolean;
	maxTravelDistance?: number;
	regularAvailabilities: Array<IRegularAvailabilities>;
	nightShift: boolean;
	weekendShift: boolean;
	teamPreference: TeamPreference;
	schedule: Schedule;
	contractPreference: ContractPreference;
	environment: Environment;
	nationalityCode?: string;
	currentAgency?: IWorkerAgency;
	interimAgencies?: Partial<IWorkerAgency[]>;
	role: string;
}

export interface ICandidateDB extends ICandidate, DB {}

export interface InactiveItem {
	timestamp: Timestamp;
	reasonSlug: string;
	complement?: string;
}

export interface InactiveByAgencies {
	[key: string]: InactiveItem;
}

export interface IIdentificationResultCommonAgency {
	id?: ObjectID;
	name: string;
	address?: IAddress;
	deleted: boolean;
	inactive: boolean;
	candidateStatus?: string;
	inactiveReason?: {
		timestamp: Timestamp;
		reasonSlug: string;
		complement?: string;
	};
}

export interface IIdentificateResultCommonWorkerSchema {
	id: ObjectID;
	firstname: string;
	lastname: string;
	birthDate: Timestamp;
	birthPlace: string;
	email: string;
	phone: string;
	address: IAddress;
	nir: string;
	nirKey: string;
	agencies: IIdentificationResultCommonAgency[];
	evoliaId?: string;
	evoliaAddress?: IAddress;
	candidateId?: ObjectID;
	workerId?: ObjectID;

	databaseOrigin: EWorkerDatabaseOrigin;
	nameSlugScore?: number;
	matchingScore?: number;
	nationalityCode: string;
	birthCountry: string;
	cv?: CV;
	picture: string;
	source: string;
	jobName: string;
	jobOfferName: string;
	duration: number;
	jobs?: IWorkerJob[];

	// Action / scenario datas
	updateProfileData: boolean;
	matchReasons: string[];
	scenario: DuplicateScenario;
	action: DuplicateResolutionAction;
}

export type CommonWorker = IIdentificateResultCommonWorkerSchema;
